import { datadogRum } from "@datadog/browser-rum";
import {
  bugsnagBootstrap,
  logError,
  setTelemetryUser,
} from "../components/bugsnag-client";
import { resolveUrl } from "../api/client";
import { FHIR_CONFIG } from "../fhir-config";
import { describeLaunchParams, scrubUrl } from "./launch-params";

// Telemetry bootstrap. Initialized from index.tsx before React renders so the
// launch routes (/launch, /app), which run before the backend is contacted, are
// instrumented too.

/** Hops of a direct FHIR launch, in order. */
export const LaunchStage = {
  BOOTSTRAP: "bootstrap",
  /** /launch — iss/launch handed off to the EMR's authorize endpoint. */
  LAUNCH_REDIRECT: "launch_redirect",
  /** /app — SMART.ready() exchanges the auth code for a token. */
  TOKEN_EXCHANGE: "token_exchange",
  /** /app — EMR context read out of the token response. */
  CONTEXT_EXTRACTION: "context_extraction",
  /** /app -> /?… — resolved context handed to the QCard. */
  CONTEXT_HANDOFF: "context_handoff",
} as const;

export type LaunchStageValue = typeof LaunchStage[keyof typeof LaunchStage];

let rumInitialized = false;
let telemetryInitialized = false;

// Mirrors the backend's release_stage (settings.ENV). From the hostname, not
// NODE_ENV: one production bundle serves qa, staging and prod.
export const getReleaseStage = (): string => {
  const host = window.location.hostname;
  if (host.includes("localhost") || host.includes("127.0.0.1")) return "dev";
  if (host.includes("-staging")) return "staging";
  if (host.includes("qa")) return "qa";
  return "prod";
};

export const getStackName = (): string => window.location.host.split(".")[0];

const isLaunchRoute = (): boolean =>
  /\/(launch|app)\/?$/.test(window.location.pathname);

// Same-origin sink for launch failures. RUM and Bugsnag both post to
// third-party hosts a hospital network can block; this one cannot be, since it
// is the origin the app already calls for its API.
const postBeacon = (
  level: "info" | "error",
  payload: Record<string, unknown>
): void => {
  const base = resolveUrl();
  if (!base) return;
  try {
    // text/plain keeps this a CORS simple request, so no preflight; the backend
    // parses the body as JSON regardless of content type.
    fetch(`${base}/launch-telemetry/`, {
      method: "POST",
      body: JSON.stringify({ ...payload, level }),
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      credentials: "omit",
      keepalive: true,
      mode: "cors",
    }).catch(() => undefined);
  } catch {
    // Telemetry must never break a launch.
  }
};

const baseContext = (stage: LaunchStageValue) => ({
  stage,
  stack: getStackName(),
  release_stage: getReleaseStage(),
  route: window.location.pathname,
  // On every stage, not just /launch: these are what an EMR rejects, and
  // fhirclient strips the query string before /app errors surface, so `params`
  // is empty by then.
  client_id: FHIR_CONFIG.clientId,
  redirect_uri: FHIR_CONFIG.redirectUri,
  params: describeLaunchParams(),
});

// Applies identity from the query string. Safe to call repeatedly — the emr_*
// params only arrive once the EMR redirects back with context.
export const identifyFromUrl = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const userID = urlParams.get("emr_user_id");
  const emrPatientID = urlParams.get("emr_patient_id");

  setTelemetryUser({
    id: userID,
    name: urlParams.get("emr_user_full_name"),
    email: urlParams.get("emr_user_email"),
  });

  if (!rumInitialized) return;
  datadogRum.setUser({
    id: userID,
    name: urlParams.get("emr_user_full_name"),
    email: urlParams.get("emr_user_email"),
    role: urlParams.get("emr_user_role"),
    service_area: urlParams.get("emr_user_service_area"),
    dept_speciality: urlParams.get("emr_user_dept_speciality"),
  });
  const stackName = getStackName();
  datadogRum.setGlobalContextProperty("stack_name", stackName);
  datadogRum.setGlobalContextProperty("customer", stackName);
  datadogRum.setGlobalContextProperty("emr_patient_id", emrPatientID);
};

const initRum = (): void => {
  const applicationId = process.env.REACT_APP_DATADOG_APP_ID;
  const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;
  // Local builds ship no RUM credentials, and init() without them makes the
  // SDK log an error on every page load.
  if (!applicationId || !clientToken) return;

  datadogRum.init({
    applicationId,
    clientToken,
    site: "datadoghq.com",
    service: "patient_embed",
    env: getReleaseStage(),
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    usePartitionedCrossSiteSessionCookie: true,
    useSecureSessionCookie: true,
    // RUM captures the view/resource URLs on its own, before fhirclient
    // strips the query string, so redact them here too.
    beforeSend: (event) => {
      event.view.url = scrubUrl(event.view.url) ?? event.view.url;
      if (event.view.referrer) {
        event.view.referrer =
          scrubUrl(event.view.referrer) ?? event.view.referrer;
      }
      if (event.type === "resource") {
        event.resource.url = scrubUrl(event.resource.url) ?? event.resource.url;
      }
      return true;
    },
  });
  rumInitialized = true;
  datadogRum.startSessionReplayRecording();
};

/** Start RUM and Bugsnag. Called once from index.tsx before React renders. */
export const initTelemetry = (): void => {
  if (telemetryInitialized) return;
  telemetryInitialized = true;

  initRum();
  bugsnagBootstrap(getReleaseStage(), getStackName());
  identifyFromUrl();
};

/** Record a launch milestone. */
export const logLaunchEvent = (
  stage: LaunchStageValue,
  message: string,
  context: Record<string, unknown> = {}
): void => {
  const payload = { ...baseContext(stage), ...context, message };
  // eslint-disable-next-line no-console
  console.info(`[launch:${stage}] ${message}`, payload);
  if (rumInitialized) datadogRum.addAction(`launch.${stage}`, payload);
  // Only the beacon is gated to launch routes, so a QCard page view is not a
  // log line; console and RUM record every milestone.
  if (isLaunchRoute()) postBeacon("info", payload);
};

/** Record a launch failure to RUM and Bugsnag, not just local state. */
export const logLaunchError = (
  stage: LaunchStageValue,
  error: unknown,
  context: Record<string, unknown> = {}
): void => {
  const err = error instanceof Error ? error : new Error(String(error));
  const payload = { ...baseContext(stage), ...context };
  // eslint-disable-next-line no-console
  console.error(`[launch:${stage}] ${err.message}`, payload);
  if (rumInitialized) datadogRum.addError(err, payload);
  logError(err, payload);
  postBeacon("error", { ...payload, message: err.message });
};
